import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setCheckOutConfirmIsOpen,
  setCheckOutConfirmRoomNumber,
  setCheckOutBookings,
} from "redux/checkOutConfirmInfoSlice";
import { useMutation } from "@apollo/client";
import { UPDATE_ROOM_STATUS } from "graphql/service";
import Tooltip from "rc-tooltip";
import Dropdown from "components/Dropdown";
import Button from "components/Button";
import statusMap from "constants/statusMap";
import { useLongPress } from "customHook/useLongPress";
//import { useGlobalMouseStopped } from "customHook/useGlobalMouseStopped";
import { useClickOutside } from "customHook/useClickOutside";
import {
  OverlayContainer,
  Container,
  DefaultIconContainer,
  Border,
} from "./styles";
import colors from "constants/colors";
import {
  UpArrowIcon,
  DownArrowIcon,
  StairwellIcon,
  LoungeIcon,
  LaundryIcon,
  CheckInTodayIcon,
  SaunaRoomIcon,
  FemaleIcon,
  MaleIcon,
  GymIcon,
  RoomNumberIcon,
  RoomTypeIcon,
  GuestInIcon,
  GuestOutIcon,
  BookingStatusIcon,
  UpcomingGuestIcon,
  CheckInTimeIcon,
  OutOfServiceIcon,
} from "assets/icons";
import { debouncedErrorMessage } from "utils/messageUtil";
import moment from "moment-timezone";
import { TIME_ZONE } from "constants/constant";

const Room = ({
  width,
  height,
  roomData = {},
  disabled,
  className,
  roomName,
  floorBackgroundColor,
  roomNumber,
  wheelchairAccessible,
}) => {
  const navigate = useNavigate();
  //const isStopped = useGlobalMouseStopped();
  const [isHoveringRoom, setIsHoveringRoom] = useState(false);
  //const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const { popUpRef, buttonRef } = useClickOutside(() => {
    setTooltipVisible(false);
  });

  //const timerRef = useRef(null);
  //const hideTimer = useRef(null);

  const dispatch = useDispatch();
  const [needsFix, setNeedsFix] = useState(false);

  useEffect(() => {
    // Detect WebKit browsers (Safari + Chrome on iOS)
    const isWebKit =
      /AppleWebKit/.test(navigator.userAgent) &&
      !/Chrome\/(?!.*Mobile)/.test(navigator.userAgent);

    // Alternative: Test for iOS rendering behavior
    const isIOSChrome = /CriOS/.test(navigator.userAgent);
    const isIOSFirefox = /FxiOS/.test(navigator.userAgent);
    const isIOSWebView = /WebKit.*Mobile/.test(navigator.userAgent);

    setNeedsFix(isWebKit || isIOSChrome || isIOSFirefox || isIOSWebView);
  }, []);

  const [currentBooking, setCurrentBooking] = useState(null);
  const [previousBooking, setPreviousBooking] = useState(null);
  const [upcomingBooking, setUpcomingBooking] = useState(null);
  const [checkInToday, setCheckInToday] = useState(false);

  const { roomStatusHistories, roomId, roomBookings, roomType } = roomData;
  const status = roomStatusHistories ? roomStatusHistories?.[0]?.status : null;

  const hasHover = window.matchMedia("(hover: hover)").matches;

  useEffect(() => {
    if (roomBookings && roomBookings.length > 0) {
      const currentIndex = roomBookings?.findIndex(
        (roomBooking) => roomBooking.isCurrent === true
      );
      if (currentIndex !== -1) {
        if (status?.includes("OCCUPIED")) {
          const currentBooking = roomBookings[currentIndex]?.booking;
          const previousBooking = roomBookings[currentIndex + 1]?.booking;
          const upcomingBooking = roomBookings[currentIndex - 1]?.booking;
          setCurrentBooking(currentBooking);
          setPreviousBooking(previousBooking);
          setUpcomingBooking(upcomingBooking);
        } else {
          const previousBooking = roomBookings[currentIndex]?.booking;
          const upcomingBooking = roomBookings[currentIndex - 1]?.booking;
          setCurrentBooking(null);
          setPreviousBooking(previousBooking);
          setUpcomingBooking(upcomingBooking);
        }
      }
    }
  }, [roomBookings, status]);

  const [updateRoomStatus] = useMutation(UPDATE_ROOM_STATUS, {
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message;
        if (error.message.includes("Validation")) {
          content = JSON.stringify(
            error.graphQLErrors[0]?.extensions?.exception
          );
        }
        debouncedErrorMessage(content);
      }
    },
  });

  const handlePageJump = () => {
    if (disabled) return;
    if (roomNumber) {
      navigate(`/room-details/${roomNumber}`);
    }
  };

  const longPressEvents = useLongPress(handlePageJump);

  const handleClick = () => {
    if (disabled) return;
    setTooltipVisible(!tooltipVisible);
  };

  // const handleDoubleClick = () => {
  //   if (disabled) return;
  //   clearTimeout(timerRef.current);
  //   handlePageJump();
  // };

  const handleMouseEnterRoom = () => {
    if (hasHover) {
      setIsHoveringRoom(true);
      //dispatch(setSelectedRoom(roomNumber))
    }
  };

  const handleMouseLeaveRoom = (e) => {
    if (hasHover) {
      setIsHoveringRoom(false);
    }
  };

  // const handleMouseEnterTooltip = () => {
  //   if(hasHover) {
  //     setIsHoveringTooltip(true);
  //   }
  // };

  // const handleMouseLeaveTooltip = (e) => {
  //   if(hasHover) {
  //     setIsHoveringTooltip(false);
  //   }
  // };

  let backgroundColor;
  switch (status) {
    case "DIRTY":
      backgroundColor = colors.yellow;
      break;
    case "CLEAN":
      backgroundColor = colors.lightGreen;
      break;
    case "INSPECTED":
      backgroundColor = colors.blue;
      break;
    case "OCCUPIED":
      backgroundColor = colors.purple;
      break;
    case "OCCUPIED_STAYOVER":
      backgroundColor = colors.red;
      break;
    case "OUT_OF_ORDER":
      backgroundColor = colors.darkGrey;
      break;
    case undefined:
      backgroundColor = colors.darkGrey;
      break;
    default:
      backgroundColor = colors.silver;
  }

  const colorReverse = !(
    backgroundColor === colors.blue || backgroundColor === colors.darkGrey
  );

  const RoomDefaultIcon = () => {
    if (status === "OUT_OF_ORDER") {
      return (
        <DefaultIconContainer
          width="15px"
          height="15px"
          colorReverse={colorReverse}
        >
          <OutOfServiceIcon className="icon" />
        </DefaultIconContainer>
      );
    } else if (checkInToday) {
      return (
        <DefaultIconContainer
          width="15px"
          height="15px"
          colorReverse={colorReverse}
        >
          <CheckInTodayIcon className="icon" />
        </DefaultIconContainer>
      );
    } else if (roomName?.length > 0) {
      switch (roomName) {
        case "Stairwell":
          return (
            <DefaultIconContainer
              width="20px"
              height="20px"
              colorReverse={colorReverse}
            >
              <StairwellIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Lounge":
          return (
            <DefaultIconContainer
              width="25px"
              height="25px"
              colorReverse={colorReverse}
            >
              <LoungeIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Laundry":
          return (
            <DefaultIconContainer
              width="20px"
              height="20px"
              colorReverse={colorReverse}
            >
              <LaundryIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Steam Room":
        case "Sauna":
          return (
            <DefaultIconContainer
              width="15px"
              height="15px"
              colorReverse={colorReverse}
            >
              <SaunaRoomIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Women's Changeroom":
        case "Men's Changeroom":
        case "Gym":
          return (
            <DefaultIconContainer
              width="30px"
              height="30px"
              colorReverse={colorReverse}
            >
              {roomName === "Women's Changeroom" ? (
                <FemaleIcon className="icon" />
              ) : roomName === "Men's Changeroom" ? (
                <MaleIcon className="icon" />
              ) : roomName === "Gym" ? (
                <GymIcon className="icon" />
              ) : null}
            </DefaultIconContainer>
          );
        case "LeftElevator":
        case "RightElevator":
          return (
            <DefaultIconContainer
              width="10px"
              height="10px"
              colorReverse={colorReverse}
            >
              {roomName === "LeftElevator" ? (
                <UpArrowIcon className="icon" />
              ) : (
                <DownArrowIcon className="icon" />
              )}
            </DefaultIconContainer>
          );
        default:
          return null;
      }
    }
  };

  const statusOptions = [
    "Room Inspected",
    "Room Clean",
    "Room Dirty",
    "Out of Service",
  ];

  const handleChangeRoomStatus = (e) => {
    const statusKey = Object.keys(statusMap).find(
      (key) => statusMap[key] === e
    );
    updateRoomStatus({
      variables: {
        input: {
          roomId: roomId,
          status: statusKey,
        },
      },
    });
  };

  const handleClickRoomNumber = (event) => {
    if (event.metaKey || event.ctrlKey) {
      // Open new tab with absolute URL
      window.open(
        `https://pms.rookhotel.com/room-details/${roomNumber}`,
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      // Normal navigation
      navigate(`/room-details/${roomNumber}`);
    }
  };

  const handleClickCurrentGuestName = (event) => {
    if (event.metaKey || event.ctrlKey) {
      // Open new tab with absolute URL
      window.open(
        `https://pms.rookhotel.com/booking-details/${currentBooking.group.groupId}`,
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      // Normal navigation
      navigate(`/booking-details/${currentBooking.group.groupId}`);
    }
  };

  const handleClickPreviousGuestName = (event) => {
    if (event.metaKey || event.ctrlKey) {
      // Open new tab with absolute URL
      window.open(
        `https://pms.rookhotel.com/booking-details/${previousBooking.group.groupId}`,
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      // Normal navigation
      navigate(`/booking-details/${previousBooking.group.groupId}`);
    }
  };

  const handleClickUpcomingGuestName = (event) => {
    if (event.metaKey || event.ctrlKey) {
      // Open new tab with absolute URL
      window.open(
        `https://pms.rookhotel.com/booking-details/${upcomingBooking.group.groupId}`,
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      // Normal navigation
      navigate(`/booking-details/${upcomingBooking.group.groupId}`);
    }
  };

  const formatString = (input) => {
    return (
      input.charAt(0).toUpperCase() +
      input.slice(1).toLowerCase().replace("_", "-")
    );
  };

  return (
    <>
      <style>
        {`
          .${
            "tooltip-root" + roomNumber
          }.rc-tooltip-placement-top > .rc-tooltip-arrow {
            border-top-color: ${backgroundColor} !important;
          }
        
          .${
            "tooltip-root" + roomNumber
          }.rc-tooltip-placement-right > .rc-tooltip-arrow {
            border-right-color: ${backgroundColor} !important;
          }
        
          .${
            "tooltip-root" + roomNumber
          }.rc-tooltip-placement-bottom > .rc-tooltip-arrow {
            border-bottom-color: ${backgroundColor} !important;
          }
        
          .${
            "tooltip-root" + roomNumber
          }.rc-tooltip-placement-left > .rc-tooltip-arrow {
            border-left-color: ${backgroundColor} !important;
          }
        `}
      </style>
      <Tooltip
        classNames={{ root: "tooltip-root" + roomNumber }}
        placement="bottom"
        visible={disabled ? false : tooltipVisible}
        //onVisibleChange={handleVisibleChange}
        destroyTooltipOnHide={true}
        overlay={
          <OverlayContainer
            backgroundColor={backgroundColor}
            ref={popUpRef}
            // onMouseEnter={handleMouseEnterTooltip}
            // onMouseLeave={handleMouseLeaveTooltip}
          >
            <Dropdown
              mainPage
              options={statusOptions}
              value={statusMap[status]}
              disabled={statusMap[status]?.includes("Occupied")}
              onChange={handleChangeRoomStatus}
              title="ROOM STATUS"
            />
            {roomName ? (
              <div className="info">
                <span className="field-name">
                  <RoomNumberIcon />
                  {"Room Name"}
                </span>
                <span className="field-value">{roomName}</span>
              </div>
            ) : (
              <>
                <div className="info">
                  <span className="field-name">
                    <RoomNumberIcon />
                    {"Room Number"}
                  </span>
                  <span
                    className="field-value highlight"
                    onClick={handleClickRoomNumber}
                  >
                    {roomNumber}
                  </span>
                </div>
                <div className="info">
                  <span className="field-name">
                    <RoomTypeIcon />
                    {"Room Type"}
                  </span>
                  <span className="field-value">{roomType?.name}</span>
                </div>
                {currentBooking ? (
                  <div className="item">
                    <div className="info">
                      <span className="field-name">
                        <GuestInIcon />
                        {"Current Guest"}
                      </span>
                      <span
                        className="field-value highlight"
                        onClick={handleClickCurrentGuestName}
                      >
                        {currentBooking.fullName}
                      </span>
                    </div>
                    <div className="info">
                      <span className="field-name">
                        <BookingStatusIcon />
                        {"Booking Status"}
                      </span>
                      <span className="field-value">
                        {formatString(currentBooking.status)}
                      </span>
                    </div>
                  </div>
                ) : upcomingBooking ? (
                  <div className="item">
                    <div className="info">
                      <span className="field-name">
                        <UpcomingGuestIcon />
                        {"Upcoming Guest"}
                      </span>
                      <span
                        className="field-value highlight"
                        onClick={handleClickUpcomingGuestName}
                      >
                        {upcomingBooking?.fullName}
                      </span>
                    </div>
                    <div className="info">
                      <span className="field-name">
                        <CheckInTimeIcon />
                        {"Check-in"}
                      </span>
                      <span className="field-value">
                        {moment(upcomingBooking?.checkInDate)
                          .tz(TIME_ZONE)
                          .isSame(moment().tz(TIME_ZONE), "day")
                          ? "Today" +
                            moment(upcomingBooking?.checkInDate)
                              .tz(TIME_ZONE)
                              .format(" - h:mmA")
                          : moment(upcomingBooking?.checkInDate)
                              .tz(TIME_ZONE)
                              .format("MMM Do YYYY - h:mmA")}
                      </span>
                    </div>
                  </div>
                ) : previousBooking ? (
                  <div className="item">
                    <div className="info">
                      <span className="field-name">
                        <GuestOutIcon />
                        {"Previous Guest"}
                      </span>
                      <span
                        className="field-value highlight"
                        onClick={handleClickPreviousGuestName}
                      >
                        {previousBooking?.fullName}
                      </span>
                    </div>
                    <div className="info">
                      <span className="field-name">
                        <BookingStatusIcon />
                        {"Booking Status"}
                      </span>
                      <span className="field-value">
                        {formatString(previousBooking.status)}
                      </span>
                    </div>
                  </div>
                ) : null}
                {status?.includes("OCCUPIED") && (
                  <div className="buttonContainer">
                    <Button
                      danger
                      additionalstyle={`
                        width: 86px;
                      `}
                      onClick={() => {
                        dispatch(setCheckOutConfirmRoomNumber(roomNumber));
                        dispatch(setCheckOutBookings([currentBooking]));
                        dispatch(setCheckOutConfirmIsOpen(true));
                      }}
                    >
                      Check Out
                    </Button>
                  </div>
                )}
              </>
            )}
          </OverlayContainer>
        }
      >
        <Container
          ref={buttonRef}
          className={className}
          roomName={roomName}
          selected={disabled ? false : isHoveringRoom}
          needsFix={needsFix}
          colorReverse={colorReverse}
          roomNumber={roomNumber}
          disabled={disabled}
          {...longPressEvents}
          onClick={handleClick}
          //onDoubleClick={handleDoubleClick}
          onMouseEnter={handleMouseEnterRoom}
          onMouseLeave={handleMouseLeaveRoom}
          backgroundColor={backgroundColor}
          floorBackgroundColor={floorBackgroundColor}
          width={width}
          height={height}
          wheelchairAccessible={wheelchairAccessible}
        >
          <div className="roomContent">
            {roomName ? null : <div className="roomNumber">{roomNumber}</div>}
            {RoomDefaultIcon()}
          </div>
        </Container>
      </Tooltip>
      {isHoveringRoom ? <Border roomNumber={roomNumber} /> : null}
    </>
  );
};

export default Room;
